import type { BaseEntity, SimpleTableProps, FieldValue } from "../types";
import type { SimpleTableSetupResult } from "../SimpleTableSetup";
import type {
  SimpleTableContextValue,
  SimpleTableStableValue,
} from "../contexts/SimpleTableContext";

/**
 * The STABLE slice of the SimpleTable context value — everything the row/cell
 * hot path reads that does NOT change on interaction.
 *
 * Kept separate from `chrome-slice.ts` so a re-rendered toolbar node, an
 * `isFetchingNextPage` toggle or a page change cannot push a new value at the
 * ~35 virtualized rows. See the partition doc on `SimpleTableStableValue`.
 */

type StableProps<TData extends BaseEntity> = Partial<SimpleTableProps<TData>> &
  Pick<SimpleTableProps<TData>, "hooks">;

/**
 * The context exposes an UNKEYED payload signature so cell editors can call
 * with partial updates; `SimpleTableProps.onInlineEdit` is typed
 * `(item: TData) => Promise<void>`. Adapt at this one place so the pure builder
 * and the memoizing hook cannot drift.
 */
export function adaptInlineEdit<TData extends BaseEntity>(
  onInlineEdit: StableProps<TData>["onInlineEdit"]
): SimpleTableContextValue<TData>["onInlineEdit"] {
  if (!onInlineEdit) return undefined;
  const typed = onInlineEdit as (item: TData) => Promise<void>;
  return (payload: Record<string, FieldValue>) => typed(payload as unknown as TData);
}

export function buildStableSlice<TData extends BaseEntity>(
  setup: SimpleTableSetupResult<TData>,
  callbacksSlice: SimpleTableContextValue<TData>["callbacks"],
  props: StableProps<TData>,
  onInlineEdit: SimpleTableContextValue<TData>["onInlineEdit"]
): SimpleTableStableValue<TData> {
  const {
    config,
    visibleFields,
    data,
    appUser,
    roleName,
    containerRef,
    tableHeaderScrollRef,
    tableBodyRef,
  } = setup;

  return {
    config,
    visibleFields,
    data,
    callbacks: callbacksSlice,
    refs: {
      containerRef,
      tableHeaderScrollRef,
      tableBodyRef,
    },
    auth: {
      appUser,
      roleName,
    },
    hooks: props.hooks,
    onEdit: props.onEdit,
    onView: props.onView,
    onDelete: props.onDelete,
    onInlineEdit,
    onOpenDetailModal: props.onOpenDetailModal,
  };
}
